import type { CreatureIntegerRollupDependencies } from './creature-integer-rollup';
import { createCreatureIntegerRollupCompiler } from './creature-integer-rollup';
import type { FromBasePlayerInput, FromBaseRow } from './from-base-context';
import { fromBaseRecord, FromBaseInputMissing, FromBaseStageMissing } from './from-base-context';
import type { FromBasePlayerGrantMetadata } from './from-base-player-grants';
import { createFromBasePlayerGrantContext, constructPlayerThroughNestedGrantsFromBase } from './from-base-player-grants';
import { phpJsonInt, phpJsonString } from './php-json-values';
import { AuraOwnerGateUnavailable, readCreatureStatuses } from './creature-status-reader';
import { readInventoryField } from './inventory-raw-readers';
import { nativeSignatureTypes, createPlSignatureJsonSources } from './pl-signature';
import { createFromBasePlayerStageContext } from './from-base-player-stages';

export interface FromBasePlayerIntegerMetadata extends FromBasePlayerGrantMetadata {
    rollup: CreatureIntegerRollupDependencies['metadata'] & Pick<CreatureIntegerRollupDependencies,
        'integerLayers' | 'sensesNames' | 'volatileKeys' | 'damagePercentKeys' | 'inventoryAbilityTypes'>;
}

/** Saved instance identities must never be mistaken for authored definitions. */
export function createFromBasePlayerSourceTypeReader(base: FromBasePlayerInput, metadata: FromBasePlayerIntegerMetadata, input: FromBaseRow) {
    const { rawDefinition: definition, readers } = createFromBasePlayerStageContext(base, metadata);
    return (id: string): unknown => {
        const same = (value: unknown) => phpJsonString(value).toUpperCase() === id.toUpperCase();
        if (same(readers.layerValue(base.player_characters, 'item_id'))) return 'player_character';
        if (same(readers.layerValue(base.encounter_creatures ?? {}, 'encounter_creature_id'))) return 'creature';
        const inventory = base.inventory?.find(row => same(readInventoryField(row, 'inventory_id')));
        if (inventory) return readers.itemField(definition(phpJsonString(readInventoryField(inventory, 'inventory_item_id'))), 'item_type') ?? '';
        for (const mode of ['target', 'caster'])
            if (Object.values(readCreatureStatuses(input, mode, definition)).some(row => fromBaseRecord(row) && same(readers.layerValue(row, 'status_id'))))
                return 'status_effect';
        return readers.itemField(definition(id), 'item_type') ?? '';
    };
}

/** Fresh browser rollup has no cross-request carry or PHP-computed inputs.
 * Full skills and their natural-weapon/derived-ability duties remain a named
 * producer gap; this window cannot certify the combined finalizer stage. */
export function constructPlayerIntegersFromBase(base: FromBasePlayerInput, metadata: FromBasePlayerIntegerMetadata, input: FromBaseRow): FromBaseRow {
    if (!metadata.rollup) throw new FromBaseInputMissing('integer-rollup-metadata', base.entity_id);
    const { definition, readers, deps } = createFromBasePlayerGrantContext(base, metadata, input, 'integer-rollup-full-skills');
    const itemType = createFromBasePlayerSourceTypeReader(base, metadata, input);
    const compile = createCreatureIntegerRollupCompiler({ ...deps, ...metadata.rollup, metadata: metadata.rollup,
        allEssences: metadata.preliminary.allEssences,
        queryTemplateDefinition: definition,
        queryItemType: itemType,
        signatureValue: payload => nativeSignatureTypes(payload as FromBaseRow, createPlSignatureJsonSources(
            { ...metadata.stageSources, ...base.catalog as Readonly<Record<string, FromBaseRow>> },
            base.inventory_metadata?.definitionAssets?.vfxRows ?? metadata.inventoryMetadata?.definitionAssets?.vfxRows ?? [])),
        source: kind => { throw new FromBaseStageMissing('integer-rollup-full-skills', `collector:${kind}`); },
    });
    const host = compile(input).row as FromBaseRow;
    const computed = (host.computed ??= {}) as FromBaseRow;
    const creature = (computed.creature ??= {}) as FromBaseRow;
    creature.meta = { ...(fromBaseRecord(creature.meta) ? creature.meta : {}), max_summons: phpJsonInt(readers.layerValue(host, 'max_summons')) || 3 };
    return host;
}

export function constructPlayerThroughIntegersFromBase(base: FromBasePlayerInput, metadata: FromBasePlayerIntegerMetadata) {
    const grants = constructPlayerThroughNestedGrantsFromBase(base, metadata);
    if (grants.kind !== 'constructed-prefix') return grants;
    try {
        return { ...grants, row: constructPlayerIntegersFromBase(base, metadata, grants.row as FromBaseRow),
            completedStages: [...grants.completedStages, 'integer-rollup-projection'],
            // Keep the combined stage until the complete full-skill producer exists.
            missingStages: grants.missingStages };
    } catch (error) {
        if (error instanceof FromBaseInputMissing) return { kind: 'missing-input' as const, stage: 'integer-rollup-full-skills', dependency: error.dependency, id: error.id };
        if (error instanceof FromBaseStageMissing) return { kind: 'missing-stage' as const, stage: error.stage, detail: error.detail };
        if (error instanceof AuraOwnerGateUnavailable) return { kind: 'missing-stage' as const, stage: 'integer-rollup-full-skills', detail: error.reason };
        throw error;
    }
}
